<template>
    <v-dialog :value="value" max-width="560" :persistent="loading" @input="$emit('input', $event)">
        <v-card class="ui-card">
            <div class="ui-card__header">
                <div>
                    <h3 class="ui-section-title">Baixar documentos da análise de ofício (.zip)</h3>
                    <p class="ui-section-description">
                        <template v-if="creditor">Documentos de <strong>{{ creditor.name }}</strong>.</template>
                        <template v-else>Documentos de todos os credores do caso.</template>
                    </p>
                </div>
            </div>
            <div class="ui-card__body">
                <p class="ui-small mb-2">Entram no arquivo:</p>
                <ul class="ui-small mb-3 archive-list">
                    <li>arquivos recebidos nas solicitações de documentos da Central, enviados pelo credor ou pela devedora;</li>
                    <li>manifestações da devedora nas análises de ofício.</li>
                </ul>
                <div class="ui-label">Pastas</div>
                <div class="ui-small archive-folders">
                    <div v-if="!creditor"><v-icon x-small class="mr-1">mdi-folder-outline</v-icon>Credor (CPF-CNPJ)/</div>
                    <div v-for="folder in folders" :key="folder">
                        <v-icon x-small class="mr-1">mdi-folder-outline</v-icon>{{ folder }}/
                    </div>
                </div>

                <div v-if="loading" class="archive-progress mt-5">
                    <div class="d-flex align-center mb-2">
                        <span class="ui-small">{{ progressLabel }}</span>
                        <v-spacer></v-spacer>
                        <span v-if="percent !== null" class="ui-small font-weight-medium">{{ percent }}%</span>
                    </div>
                    <v-progress-linear :indeterminate="percent === null" :value="percent || 0" height="8" rounded color="primary" background-color="grey lighten-3" />
                </div>
            </div>
            <div class="ui-card__footer">
                <v-btn outlined :disabled="loading" @click="$emit('input', false)">Cancelar</v-btn>
                <v-btn color="primary" depressed :loading="loading" @click="download">
                    <v-icon left small>mdi-folder-zip-outline</v-icon>
                    Gerar .zip
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";

// Central de Creditos: .zip dos documentos da analise de oficio, do caso ou de um credor.
// O servidor monta o arquivo primeiro (barra indeterminada) e depois envia (barra em %).
export default {
    name: "CreditAnalysisArchiveDialog",

    props: {
        value: { type: Boolean, default: false },
        clientId: { type: [Number, String], default: null },
        // { name, registration } para baixar so um credor; vazio = caso inteiro.
        creditor: { type: Object, default: null },
    },

    data: () => ({
        loading: false,
        loaded: 0,
        total: 0,
        folders: ["Enviados pelo credor", "Enviados pela devedora", "Manifestação da devedora"],
    }),

    computed: {
        percent() {
            if (!this.total) return null;
            return Math.min(100, Math.round((this.loaded / this.total) * 100));
        },
        progressLabel() {
            if (!this.loaded) return "Gerando o arquivo no servidor...";
            const mb = (value) => (value / (1024 * 1024)).toFixed(1).replace(".", ",");
            return this.total ? `Baixando ${mb(this.loaded)} de ${mb(this.total)} MB` : `Baixando ${mb(this.loaded)} MB`;
        },
    },

    watch: {
        value(open) {
            if (open) this.reset();
        },
    },

    methods: {
        reset() {
            this.loading = false;
            this.loaded = 0;
            this.total = 0;
        },

        async download() {
            if (!this.clientId) return;
            this.reset();
            this.loading = true;
            try {
                const params = { client_id: this.clientId };
                if (this.creditor && this.creditor.registration) params.registration = this.creditor.registration;
                const result = await axios.get("/api/admin/credit-analysis/archive", {
                    params,
                    responseType: "blob",
                    onDownloadProgress: (event) => {
                        this.loaded = event.loaded || 0;
                        this.total = event.total || 0;
                    },
                });
                const disposition = result.headers["content-disposition"] || "";
                const match = disposition.match(/filename="([^"]+)"/);
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = match ? decodeURIComponent(match[1]) : "Analise_de_oficio.zip";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.$emit("input", false);
            } catch (error) {
                // Erro da API chega como Blob quando responseType = blob.
                if (error.response && error.response.data instanceof Blob) {
                    try {
                        error.response.data = JSON.parse(await error.response.data.text());
                    } catch (parseError) {
                        // mantem o erro original
                    }
                }
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.archive-list {
    padding-left: 18px;
}

.archive-folders {
    line-height: 1.7;
}

.archive-progress {
    padding: 12px 14px;
    border: 1px solid #e6e2ee;
    border-radius: 8px;
    background: #fafafc;
}
</style>
